import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, MapPin, Clock, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlanBoard({ user }) {
  const { eventId, planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlanData();
  }, [planId]);

  const fetchPlanData = async () => {
    try {
      const [assignmentsRes, locationsRes, photographersRes] = await Promise.all([
        axios.get(`${API}/plans/${planId}/assignments`),
        axios.get(`${API}/events/${eventId}/locations`),
        axios.get(`${API}/photographers`)
      ]);
      setAssignments(assignmentsRes.data);
      setLocations(locationsRes.data);
      setPhotographers(photographersRes.data);
    } catch (error) {
      toast.error('Failed to fetch plan data');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    try {
      await axios.post(`${API}/plans/${planId}/publish`);
      toast.success('Plan published successfully!');
    } catch (error) {
      toast.error('Failed to publish plan');
    }
  };

  const handleGenerateAI = async () => {
    try {
      toast.info('Generating AI plan...');
      const response = await axios.post(`${API}/ai/generate-plan?event_id=${eventId}&plan_name=AI Generated`);
      toast.success('AI plan generated!');
      console.log('AI Response:', response.data);
    } catch (error) {
      toast.error('Failed to generate AI plan');
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar user={user} />
        <div className="main-content flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar user={user} />
      <div className="main-content flex-1">
        <div className="page-header flex items-center justify-between">
          <div>
            <h1 className="page-title">Plan Board</h1>
            <p className="page-subtitle">Assign photographers to locations and time slots</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleGenerateAI} variant="outline" className="border-2" data-testid="generate-ai-btn">
              <Sparkles className="w-5 h-5 mr-2" />
              AI Generate
            </Button>
            <Button onClick={handlePublish} className="btn-gradient" data-testid="publish-plan-btn">
              Publish Plan
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="glass-card p-6">
            <MapPin className="w-8 h-8 text-emerald-600 mb-3" />
            <p className="text-2xl font-bold">{locations.length}</p>
            <p className="text-slate-600 text-sm">Locations</p>
          </Card>
          <Card className="glass-card p-6">
            <Users className="w-8 h-8 text-blue-600 mb-3" />
            <p className="text-2xl font-bold">{photographers.length}</p>
            <p className="text-slate-600 text-sm">Photographers</p>
          </Card>
          <Card className="glass-card p-6">
            <Clock className="w-8 h-8 text-purple-600 mb-3" />
            <p className="text-2xl font-bold">{assignments.length}</p>
            <p className="text-slate-600 text-sm">Assignments</p>
          </Card>
        </div>

        <Card className="glass-card p-6">
          <h2 className="text-xl font-semibold mb-4">Assignments</h2>
          {assignments.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600 mb-4">No assignments yet</p>
              <Button onClick={handleGenerateAI} className="btn-gradient" data-testid="generate-first-ai-btn">
                <Sparkles className="w-5 h-5 mr-2" />
                Generate with AI
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="p-4 bg-white/50 rounded-lg border border-slate-200"
                  data-testid={`assignment-item-${assignment.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Location: {assignment.location_id}</p>
                      <p className="text-sm text-slate-600">Photographer: {assignment.photographer_id}</p>
                      <p className="text-sm text-slate-600">Role: {assignment.coverage_role}</p>
                    </div>
                    <span className="status-badge status-draft">
                      {assignment.validated ? 'Validated' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
